import { useContext, useEffect, useRef } from 'react';
import useCustomHook from '@/hooks/useCustomHook';
import { GlobalContext } from '@/shared/GlobalContext/GlobalContext';
import getPaymentMethods from '@/api/subscriptions/getPaymentMethods.endpoint';
import createPaymentIntent from '@/api/subscriptions/createPaymentIntent.endpoint';
import updatePaymentIntent from '@/api/subscriptions/updatePaymentIntent.endpoint';

export default function usePaymentMethods({ amount, elementsRef }) {
  const globalContext = useContext(GlobalContext);
  const amountRef = useRef(amount);
  const futurePaymentsRef = useRef(false);
  const { state, actions } = useCustomHook({
    defaultState: {
      loading: true,
      paymentMethods: [],
      mode: 'existing',
      paymentIntent: null,
      futurePayments: false,
    },
    actions: {
      setMode: (mode) => {
        actions.setState({ mode });
      },
      setFuturePayments: (futurePayments) => {
        actions.setState({ futurePayments });
      },
      loadPaymentMethods: async () => {
        let paymentMethods = [];
        try {
          actions.setState({
            loading: true,
          });
          paymentMethods = await getPaymentMethods();
          actions.setState({
            loading: false,
            paymentMethods,
            mode: paymentMethods.length ? 'existing' : 'new',
          });
        } catch (e) {
          actions.setState({
            loading: false,
            paymentMethods: [],
          });
        }
      },
      loadForm: async () => {
        try {
          actions.setState({
            loading: true,
            error: null,
          });
          const paymentIntent = await createPaymentIntent({
            amount,
            futurePayments: state.futurePayments,
          });
          actions.setState({
            loading: false,
            paymentIntent,
          });
        } catch (e) {
          actions.setState({
            loading: false,
            error: e.response?.data?.message || e.message,
          });
        }
      },
      updateForm: async () => {
        try {
          actions.setState({
            loading: true,
            error: null,
          });
          if (amountRef.current > 0) {
            await updatePaymentIntent(state.paymentIntent.id, {
              amount: amountRef.current,
              futurePayments: futurePaymentsRef.current,
            });
            await elementsRef.current?.fetchUpdates();
          }
          actions.setState({
            loading: false,
          });
        } catch (e) {
          actions.setState({
            loading: false,
            error: e.response?.data?.message || e.message,
          });
        }
      },
    },
  });

  useEffect(() => {
    if (globalContext.state.loaded && globalContext.state.user?.teacher) {
      actions.loadPaymentMethods();
    }
  }, [globalContext.state.loaded]);

  useEffect(() => {
    if (state.mode == 'new' && !state.paymentIntent) {
      actions.loadForm();
    }
  }, [state.mode, state.paymentIntent]);

  useEffect(() => {
    if (
      amountRef.current != amount ||
      futurePaymentsRef.current != state.futurePayments
    ) {
      amountRef.current = amount;
      futurePaymentsRef.current = state.futurePayments;

      if (state.paymentIntent) {
        actions.updateForm();
      } else {
        actions.loadForm();
      }
    }
  }, [amount, state.futurePayments]);

  return { state, actions };
}
